import { Farmer } from 'api/models'
import React, { useCallback, useEffect } from 'react'
import { Tabs, Tab, Spinner, Alert } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { GhgFarmDetailStep } from './GhgFarmDetailStep'
import { GhgGrazingMovementsStep } from './GhgGrazingMovementsStep'
import { GhgLivestockBalancesStep } from './GhgLivestockBalancesStep'
import { GhgLivestockMovementsStep } from './GhgLivestockMovementsStep'
import { GhgResultStep } from './GhgResultStep'
import { GhgCalculatorFormContext, useGhgCalculatorForm } from './use-ghg-calculator-form'
import { GHG_STEPS } from './ghg-calculator.nav'
import { getSeasonRange } from 'utils/season'
import { formatDateForDisplay, formatIsoDate } from 'utils/date'

interface Props {
  user: Farmer
}
export const GhgCalculator = (props: Props) => {
  const { id } = useParams<{ id?: string }>()
  const ghgCalculatorBag = useGhgCalculatorForm({ user: props.user, calculationId: id })

  const {
    currentStep,
    isInitialized,
    isLoading,
    calculationFetchErrorMessage,
    goToStep,
    currentFormBag,
  } = ghgCalculatorBag

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentStep.id])

  useEffect(() => {
    if (!ghgCalculatorBag.isInitialized || ghgCalculatorBag.isLoading) return

    // Show an error if there is a draft or saved farm without a regional_council field
    if (!ghgCalculatorBag.farmDetail.regional_council && currentStep.id !== 'farm-detail') {
      goToStep('farm-detail')
      setTimeout(() => {
        window.alert(
          "A 'Regional council' field has now been added to your GHG calculation. Please select a regional council before continuing."
        )
      })
    }

    // Go to grazing movements tab if any dates are invalid for the analysis season period
    else if (
      currentStep.id === 'result' &&
      ghgCalculatorBag.farmDetail.season_year &&
      ghgCalculatorBag.farmDetail.end_month
    ) {
      const seasonRange = getSeasonRange(
        ghgCalculatorBag.farmDetail.season_year,
        ghgCalculatorBag.farmDetail.end_month
      )
      const [startIsoDate, endIsoDate] = seasonRange.map((d) => formatIsoDate(d))

      const invalidGrazingDate =
        ghgCalculatorBag.grazingOnMovements.some(({ arrived_date, left_date }) => {
          return (
            (arrived_date && (arrived_date < startIsoDate || arrived_date > endIsoDate)) ||
            (left_date && (left_date < startIsoDate || left_date > endIsoDate))
          )
        }) ||
        ghgCalculatorBag.grazingOffMovements.some(({ departed_date, returned_date }) => {
          return (
            (departed_date && (departed_date < startIsoDate || departed_date > endIsoDate)) ||
            (returned_date && (returned_date < startIsoDate || returned_date > endIsoDate))
          )
        })

      if (invalidGrazingDate) {
        const [startDate, endDate] = seasonRange.map((d) => formatDateForDisplay(d))

        goToStep('grazing-movements')
        setTimeout(() => {
          window.alert(
            `Some grazing movement dates fall outside the ${startDate} - ${endDate} analysis season.`
          )
        })
      }
    }
  }, [ghgCalculatorBag, goToStep, currentStep])

  const handleTabChange = useCallback(
    (stepId) => {
      if (currentFormBag && currentFormBag.current && currentFormBag.current.handleSubmit) {
        currentFormBag.current.handleSubmit(
          () => {
            goToStep(stepId)
          },
          () => {
            setTimeout(() => {
              window.alert(
                'There are errors on the form, all must be fixed before switching to another tab.'
              )
            })
          }
        )()
      } else {
        goToStep(stepId)
      }
    },
    [goToStep, currentFormBag]
  )

  return (
    <GhgCalculatorFormContext.Provider value={ghgCalculatorBag}>
      <div className="row justify-content-center">
        <div className="col-10">
          <h1 className="text-center mt-5">Greenhouse Gas Emissions Calculation</h1>
        </div>
      </div>
      {isInitialized && !isLoading ? (
        calculationFetchErrorMessage ? (
          <Alert variant="danger">{calculationFetchErrorMessage}</Alert>
        ) : (
          <Tabs
            activeKey={currentStep.id}
            mountOnEnter
            unmountOnExit
            transition={false}
            onSelect={handleTabChange}
          >
            <Tab eventKey={GHG_STEPS.FARM_DETAIL.id} title={GHG_STEPS.FARM_DETAIL.title}>
              <GhgFarmDetailStep />
            </Tab>
            <Tab
              eventKey={GHG_STEPS.LIVESTOCK_BALANCES.id}
              title={GHG_STEPS.LIVESTOCK_BALANCES.title}
            >
              <GhgLivestockBalancesStep />
            </Tab>
            <Tab
              eventKey={GHG_STEPS.GRAZING_MOVEMENTS.id}
              title={GHG_STEPS.GRAZING_MOVEMENTS.title}
            >
              <GhgGrazingMovementsStep />
            </Tab>
            <Tab
              eventKey={GHG_STEPS.LIVESTOCK_MOVEMENTS.id}
              title={GHG_STEPS.LIVESTOCK_MOVEMENTS.title}
            >
              <GhgLivestockMovementsStep />
            </Tab>
            <Tab eventKey={GHG_STEPS.RESULT.id} title={GHG_STEPS.RESULT.title}>
              <GhgResultStep />
            </Tab>
          </Tabs>
        )
      ) : (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      )}
    </GhgCalculatorFormContext.Provider>
  )
}
